import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { adminMdaCodes, useAuth } from '../../lib/auth';
import type { Officer, RoleGrant } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function RoleGrants() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isHr = roles.includes('HR_LIAISON');
  const canApprove = adminMdaCodes(roles).length > 0;
  const canSee = isHr || canApprove;

  const [status, setStatus] = useState('PENDING');
  const [rejecting, setRejecting] = useState<RoleGrant | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (status) p.set('status', status);
    return `/api/v1/staffdesk/role-grants?${p}`;
  }, [status]);
  const listQ = useApiQuery<{ items: RoleGrant[] }>(canSee ? query : null);
  const officersQ = useApiQuery<{ items: Officer[] }>(canSee ? '/api/v1/staffdesk/officers?limit=100' : null);
  const officerById = useMemo(() => new Map((officersQ.data?.items ?? []).map((o) => [o.id, o])), [officersQ.data]);

  const approve = async (g: RoleGrant) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/staffdesk/role-grants/${g.id}/approve`);
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Approval failed');
    }
  };

  if (!canSee) {
    return (
      <div>
        <PageHeader title="Role grant approvals" subtitle="Maker-checker queue: HR proposes, the officer's own MDA administrator approves" />
        <EmptyState title="No access" hint="This screen needs HR_LIAISON, or an MDA_ADMIN:<code> role." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Role grant approvals" subtitle="Maker-checker queue: HR proposes, the officer's own MDA administrator approves" />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      <div className="mb-4">
        <select className="field-input max-w-[180px]" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="">All</option>
        </select>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Nothing here" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Officer</th>
                <th className="table-head">Action</th>
                <th className="table-head">Role</th>
                <th className="table-head">Requested by</th>
                <th className="table-head">Status</th>
                <th className="table-head">Requested</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((g) => {
                const officer = officerById.get(g.officerId);
                const isSelfRequest = g.requestedBy === persona?.sub;
                return (
                  <tr key={g.id} className="hover:bg-slate-50">
                    <td className="table-cell text-xs">
                      {officer ? `${officer.personId} (${officer.mdaCode} — ${officer.position})` : g.officerId}
                    </td>
                    <td className="table-cell text-xs font-semibold">{g.action}</td>
                    <td className="table-cell font-mono text-xs">{g.role}</td>
                    <td className="table-cell text-xs text-slate-500">{g.requestedBy}</td>
                    <td className="table-cell">
                      <StatusBadge status={g.status} />
                    </td>
                    <td className="table-cell text-slate-500">{formatDateTime(g.createdAt)}</td>
                    <td className="table-cell text-right">
                      {g.status === 'PENDING' && canApprove ? (
                        <div className="flex justify-end gap-1.5">
                          <button
                            className="btn-success !px-2 !py-0.5 text-xs"
                            onClick={() => approve(g)}
                            disabled={isSelfRequest}
                            title={isSelfRequest ? 'A different MDA administrator must approve this' : undefined}
                          >
                            Approve
                          </button>
                          <button className="btn-danger !px-2 !py-0.5 text-xs" onClick={() => setRejecting(g)} disabled={isSelfRequest}>
                            Reject
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {rejecting ? (
        <RejectModal
          grant={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            listQ.reload();
          }}
          onError={setActionError}
        />
      ) : null}
    </div>
  );
}

function RejectModal({
  grant,
  onClose,
  onDone,
  onError,
}: {
  grant: RoleGrant;
  onClose: () => void;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/api/v1/staffdesk/role-grants/${grant.id}/reject`, { reason });
      onDone();
    } catch (e) {
      onError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Rejection failed');
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Reject role grant — ${grant.role}`} onClose={onClose}>
      <div className="space-y-3">
        <Field label="Reason">
          <textarea className="field-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={submit} disabled={busy || reason.length < 3}>
            {busy ? 'Rejecting…' : 'Reject'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
